import { useCallback, useEffect, useRef, useState } from "react";

import type { StepUpPhase } from "../StepUpFlow";

import {
  type CreateProgramApiOptions,
  type CreateProgramErrorKind,
  type CreateProgramInput,
  type CreateProgramOutcome,
  type CreateProgramStepUp,
  type LookupResult,
  mockSubmitProgram,
  type PartnerSpec,
  type PendingCreateResume,
  type SubIB,
  type SymbolOverride,
} from "../data";

export type WizardStepId =
  | "lookup"
  | "relationship"
  | "split"
  | "review"
  | "verify"
  | "success";

export type WizardPartner = {
  partnerId: string;
  canonicalId: string;
  displayName: string;
  email: string;
  isExisting: boolean;
  identityToggle: boolean;
  comment: string;
  splitPct: number;
  kycStatus?: "Verified" | "Pending";
};

export type CreateProgramWizardResult = {
  hoidId: string;
  rebateProgramLabel: string;
  referralLink: string;
  partners: WizardPartner[];
  /**
   * rebate_program.id — what the detail and archive endpoints key off, as
   * distinct from the displayed HOID. Undefined in mock mode, where `hoidId`
   * doubles as both.
   */
  programId?: string;
  /** Creation date (YYYY-MM-DD) as the API recorded it; undefined in mock mode. */
  createdAt?: string;
};

type UseCreateProgramWizardArgs = {
  presetSub: SubIB | null;
  /** Called when the create call reports the Master is at their program cap. */
  onCapped: () => void;
  onCreated: (
    result: CreateProgramWizardResult,
    partnerSpecs: PartnerSpec[],
  ) => void;
  /**
   * Injected create mutation. When omitted the wizard falls back to
   * `mockSubmitProgram`, so Storybook / vibe-coding / tests keep working.
   */
  createProgram?: (
    input: CreateProgramInput,
    options?: CreateProgramApiOptions,
  ) => Promise<CreateProgramOutcome>;
  stepUp?: CreateProgramStepUp;
  resume?: PendingCreateResume | null;
  /**
   * Split overrides configured on the split step. They live in the wizard
   * component (they are a sub-page, not a step), so they have to be handed in
   * — before this they never reached the submit at all and were silently
   * dropped.
   */
  overrides?: SymbolOverride[];
  /** Called when the API reports the Master has no active rebate account (422). */
  onNoRebateAccount?: () => void;
  /**
   * Called when the Sub IB lineup actually changes (add/remove/swap), at the
   * same moment the split percentages reset. `overrides` are keyed by Sub IB
   * id and owned by the wizard component, not this hook, so the component
   * clears them here rather than them silently carrying over to whichever
   * Sub IB now occupies that slot.
   */
  onLineupReset?: () => void;
};

const partnerFromLookup = (result: LookupResult): WizardPartner | null => {
  if (!result.matched) return null;
  return {
    partnerId: result.partner.partnerId,
    canonicalId: result.existingRelationship.isExistingSubIb
      ? result.partner.partnerId
      : result.partner.partnerId,
    displayName: result.partner.displayName,
    email: result.partner.email,
    isExisting: result.existingRelationship.isExistingSubIb,
    identityToggle: false,
    comment: result.existingRelationship.comment,
    splitPct: 0,
    kycStatus: result.partner.kycStatus,
  };
};

const partnerFromResume = (
  partner: PendingCreateResume["partners"][number],
): WizardPartner => ({
  partnerId: partner.partnerId,
  canonicalId: partner.canonicalId,
  displayName: partner.displayName,
  email: partner.email,
  isExisting: partner.isExisting,
  identityToggle: partner.identityToggle,
  comment: partner.comment,
  splitPct: partner.splitPct,
  kycStatus: partner.kycStatus,
});

export function useCreateProgramWizard({
  presetSub,
  onCapped,
  onCreated,
  createProgram,
  stepUp,
  resume,
  overrides = [],
  onNoRebateAccount,
  onLineupReset,
}: UseCreateProgramWizardArgs) {
  // The split comes before the per-partner privacy steps, so a preset sub skips
  // the lookup gate straight into the split rather than into "relationship".
  const [step, setStep] = useState<WizardStepId>(
    resume
      ? resume.mode === "verify"
        ? "verify"
        : "review"
      : presetSub
        ? "split"
        : "lookup",
  );
  const [partners, setPartners] = useState<WizardPartner[]>(() =>
    resume
      ? resume.partners.map(partnerFromResume)
      : presetSub
        ? [
            {
              partnerId: presetSub.partnerId,
              canonicalId: presetSub.id,
              displayName: presetSub.name,
              email: presetSub.email,
              isExisting: true,
              identityToggle: false,
              comment: presetSub.comment,
              splitPct: 0,
            },
          ]
        : [],
  );
  const [relationshipIndex, setRelationshipIndex] = useState(0);
  // Restoring the Review step after a Back from Auth0: seed the split-step state
  // from the saved partners so masterShare and the split summary are correct.
  const [split, setSplit] = useState(() => resume?.partners[0]?.splitPct ?? 0);
  const [split2, setSplit2] = useState(
    () => resume?.partners[1]?.splitPct ?? 0,
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  /**
   * Which failure class the last submit hit, for callers that need to say more
   * than "something went wrong". `submitError` stays a boolean so the Review
   * step's existing error rendering is unchanged.
   */
  const [submitErrorKind, setSubmitErrorKind] =
    useState<CreateProgramErrorKind | null>(null);
  /** The Sub IB id the API rejected, when it named one (404 sub_ib_not_found). */
  const [submitErrorSubIbId, setSubmitErrorSubIbId] = useState<string | null>(
    null,
  );
  const [result, setResult] = useState<CreateProgramWizardResult | null>(null);
  /**
   * Guards against a second submit while one is in flight. A ref rather than the
   * `submitting` state because two clicks in the same tick both read the
   * pre-update state — and creation is **not** idempotent, so a double submit
   * mints a second program rather than being merely wasteful.
   */
  const inFlight = useRef(false);

  const [stepUpPhase, setStepUpPhaseState] = useState<StepUpPhase>(
    resume?.mode === "verify" ? "verifying" : "redirect",
  );
  const stepUpPhaseRef = useRef<StepUpPhase>(stepUpPhase);
  const setStepUpPhase = useCallback((phase: StepUpPhase) => {
    stepUpPhaseRef.current = phase;
    setStepUpPhaseState(phase);
  }, []);
  const stepUpRun = useRef<{
    controller: AbortController;
    released: boolean;
  } | null>(null);
  const submitAbort = useRef<AbortController | null>(null);
  /** The post-OTP replay's controller — only the user's Back/Leave cancels it. */
  const resumeAbort = useRef<AbortController | null>(null);

  const lineupTouched = useRef(false);
  const markLineupTouched = useCallback(() => {
    lineupTouched.current = true;
  }, []);

  /**
   * The lookups the gate committed. Held here rather than in `LookupStep`
   * because that step unmounts when the wizard advances, so its own state can't
   * survive a Back into the gate — the pinned partners have to be re-seeded from
   * above.
   */
  const [gateResults, setGateResults] = useState<LookupResult[]>([]);

  /**
   * Whether the gate has unsaved input. Reported up by `LookupStep`, which owns
   * the draft email fields; keeping the drafts there avoids making the whole
   * step controlled just to answer this one question.
   */
  const [gateDirty, setGateDirty] = useState(false);

  const isTwoSub = partners.length > 1;

  /** Commits 1 or 2 resolved lookups and advances past the gate — called by the
   * Lookup step's explicit Continue action, not automatically on match.
   *
   * Deliberately does NOT check the resolved partner's own program count against
   * the cap. The limit is the Master's total across every Sub IB, so comparing
   * one Sub IB's count to it blocked a Master with free slots from adding
   * another program for a busy Sub IB. The Master-total check runs before the
   * wizard opens (`requestCreateProgram`), and the create call's 409 catches a
   * limit reached while the wizard was open. */
  const commitPartners = useCallback(
    (results: LookupResult[]) => {
      const resolved = results
        .map(partnerFromLookup)
        .filter((p): p is WizardPartner => p !== null);
      if (resolved.length === 0) return;

      if (lineupTouched.current) {
        setSplit(0);
        setSplit2(0);
        onLineupReset?.();
      }
      lineupTouched.current = false;

      setGateResults(results);
      setPartners(resolved);
      setRelationshipIndex(0);
      setStep("split");
    },
    [onLineupReset],
  );

  const updateCurrentPartner = useCallback(
    (patch: Partial<Pick<WizardPartner, "identityToggle" | "comment">>) => {
      setPartners((prev) =>
        prev.map((p, i) => (i === relationshipIndex ? { ...p, ...patch } : p)),
      );
    },
    [relationshipIndex],
  );

  /**
   * Advances out of the per-partner privacy step. With the split now ahead of
   * these steps, the last partner leads to Review rather than to the split.
   */
  const goToSplit = useCallback(() => {
    if (relationshipIndex < partners.length - 1) {
      setRelationshipIndex((i) => i + 1);
      return;
    }
    setStep("review");
  }, [relationshipIndex, partners.length]);

  /** Back out of a privacy step: to the previous partner, else to the split. */
  const backFromRelationship = useCallback(() => {
    if (relationshipIndex > 0) {
      setRelationshipIndex((i) => i - 1);
      return;
    }
    setStep("split");
  }, [relationshipIndex]);

  /**
   * Commits the split percentages onto the partners and moves into the first
   * privacy step. The percentages have to be written here rather than at Review
   * because the privacy steps sit between the two now, and Review reads
   * `partners[].splitPct`.
   */
  const confirmSplit = useCallback(() => {
    setPartners((prev) =>
      prev.map((p, i) => ({
        ...p,
        splitPct: i === 0 ? split : split2,
      })),
    );
    setRelationshipIndex(0);
    setStep("relationship");
  }, [split, split2]);

  /** Back out of the split: to the lookup gate, unless the sub was preset. */
  const backFromSplit = useCallback(() => {
    if (!presetSub) setStep("lookup");
  }, [presetSub]);

  const goToVerify = useCallback(() => {
    setStep("verify");
  }, []);

  const cancelSubmit = useCallback(() => {
    submitAbort.current?.abort();
    submitAbort.current = null;
    resumeAbort.current?.abort();
    resumeAbort.current = null;
  }, []);

  const backFromVerify = useCallback(() => {
    cancelSubmit();
    const run = stepUpRun.current;
    if (run) {
      run.controller.abort();
      stepUpRun.current = null;
      if (stepUpPhaseRef.current === "redirect") {
        run.released = true;
        inFlight.current = false;
      }
    }
    setStep("review");
  }, [cancelSubmit]);

  // Declared before doSubmit, which sends masterShare as the request's
  // masterPct — the split step's own percentages are the source of truth.
  const totalSubShare = isTwoSub ? split + split2 : split;
  const masterShare = 100 - totalSubShare;

  const partnerSpecsFor = useCallback(
    (list: WizardPartner[]): PartnerSpec[] =>
      list.map((p) => ({
        canonicalId: p.canonicalId,
        isNew: !p.isExisting,
        name: p.displayName,
        email: p.email,
        kycStatus: "Verified",
        partnerId: p.partnerId,
        comment: p.comment,
      })),
    [],
  );

  const succeed = useCallback(
    (wizardResult: CreateProgramWizardResult) => {
      setSubmitting(false);
      setResult(wizardResult);
      setStep("success");
      onCreated(wizardResult, partnerSpecsFor(wizardResult.partners));
    },
    [onCreated, partnerSpecsFor],
  );

  const fail = useCallback((kind: CreateProgramErrorKind, subIbId?: string) => {
    setSubmitting(false);
    setSubmitError(true);
    setSubmitErrorKind(kind);
    setSubmitErrorSubIbId(subIbId ?? null);
    setStep("review");
  }, []);

  const showStepUpError = useCallback(() => {
    setSubmitting(false);
    setStepUpPhase("error");
    setStep("verify");
  }, [setStepUpPhase]);

  const applyOutcome = useCallback(
    (outcome: CreateProgramOutcome, { left = false } = {}) => {
      if (outcome.ok) {
        succeed({
          hoidId: outcome.program.hoidId,
          rebateProgramLabel: outcome.program.rebateProgramLabel,
          referralLink: outcome.program.referralLink,
          programId: outcome.program.programId,
          createdAt: outcome.program.createdAt,
          partners,
        });
        return;
      }

      // Step-up abandoned via bfcache Back — not a failure, just undo the
      // in-flight submit and sit back on an idle Review step.
      if (outcome.error === "step_up_cancelled") {
        setSubmitting(false);
        setStep("review");
        return;
      }

      if (
        outcome.error === "step_up_failed" ||
        outcome.error === "step_up_required"
      ) {
        if (left) {
          setSubmitting(false);
          return;
        }
        showStepUpError();
        return;
      }
      if (outcome.error === "program_cap_reached") {
        setSubmitting(false);
        onCapped();
        return;
      }
      if (outcome.error === "no_rebate_account" && onNoRebateAccount) {
        setSubmitting(false);
        onNoRebateAccount();
        return;
      }
      fail(outcome.error, outcome.subIbId);
    },
    [partners, succeed, fail, onCapped, onNoRebateAccount, showStepUpError],
  );

  const startStepUp = useCallback(() => {
    if (!stepUp) return;
    const run = { controller: new AbortController(), released: false };
    const { signal } = run.controller;
    stepUpRun.current = run;
    setSubmitting(false);
    setStepUpPhase("redirect");
    setStep("verify");

    stepUp
      .elevate(signal)
      .then(() => {
        if (signal.aborted) return undefined;
        setStepUpPhase("verifying");
        return stepUp
          .retry()
          .then((outcome) => applyOutcome(outcome, { left: signal.aborted }));
      })
      .catch(() => {
        if (!signal.aborted) showStepUpError();
      })
      .finally(() => {
        if (stepUpRun.current === run) stepUpRun.current = null;
        if (!run.released) inFlight.current = false;
      });
  }, [stepUp, applyOutcome, showStepUpError, setStepUpPhase]);

  const doSubmit = useCallback(() => {
    // Creation is not idempotent — a second in-flight call mints a second
    // program, so drop it outright rather than only disabling the button.
    if (inFlight.current) return;
    inFlight.current = true;

    setSubmitting(true);
    setSubmitError(false);
    setSubmitErrorKind(null);
    setSubmitErrorSubIbId(null);

    // No injected resource (Storybook / vibe-coding / tests) — keep the mock.
    if (!createProgram) {
      mockSubmitProgram(partners[0]?.email ?? "")
        .then((res) => {
          succeed({
            hoidId: res.hoidId,
            rebateProgramLabel: res.rebateProgramLabel,
            referralLink: res.referralLink,
            partners,
          });
        })
        .catch(() => fail("unknown"))
        .finally(() => {
          inFlight.current = false;
        });
      return;
    }

    let signal: AbortSignal | undefined;
    let onRedirect: (() => void) | undefined;
    if (stepUp) {
      const controller = new AbortController();
      submitAbort.current = controller;
      signal = controller.signal;
      onRedirect = () => {
        if (controller.signal.aborted) return;
        setStepUpPhase("redirect");
        setStep("verify");
      };
    }

    createProgram(
      {
        masterPct: masterShare,
        partners: partners.map((p) => ({
          ibId: p.partnerId,
          pct: p.splitPct,
          showName: p.identityToggle,
          showEmail: p.identityToggle,
          comment: p.comment,
        })),
        overrides,
      },
      {
        display: {
          partners: partners.map((p) => ({
            partnerId: p.partnerId,
            canonicalId: p.canonicalId,
            displayName: p.displayName,
            email: p.email,
            isExisting: p.isExisting,
            identityToggle: p.identityToggle,
            comment: p.comment,
            splitPct: p.splitPct,
            kycStatus: p.kycStatus,
          })),
          overrides,
        },
        signal,
        onRedirect,
      },
    )
      .then((outcome) => {
        if (!outcome.ok && outcome.error === "step_up_required" && stepUp) {
          startStepUp();
          return;
        }
        applyOutcome(outcome);
        inFlight.current = false;
      })
      .catch(() => {
        fail("unknown");
        inFlight.current = false;
      });
  }, [
    partners,
    masterShare,
    overrides,
    createProgram,
    stepUp,
    succeed,
    fail,
    applyOutcome,
    startStepUp,
    setStepUpPhase,
  ]);

  useEffect(
    () => () => {
      stepUpRun.current?.controller.abort();
      submitAbort.current?.abort();
    },
    [],
  );

  const resumeUnmountTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    clearTimeout(resumeUnmountTimer.current);
    return () => {
      // Deferred so StrictMode's simulated remount can cancel it; a real unmount can't.
      resumeUnmountTimer.current = setTimeout(
        () => resumeAbort.current?.abort(),
        0,
      );
    };
  }, []);

  const resumeStarted = useRef(false);
  useEffect(() => {
    if (!resume || resumeStarted.current) return;
    resumeStarted.current = true;

    if (resume.mode === "review") {
      resume.discard();
      return;
    }

    inFlight.current = true;
    const controller = new AbortController();
    resumeAbort.current = controller;
    resume
      .run(controller.signal)
      .then((outcome) => {
        if (controller.signal.aborted && !outcome.ok) {
          setSubmitting(false);
          return;
        }
        if (!outcome.ok && outcome.error === "step_up_required") {
          setSubmitting(false);
          setStep("review");
          resume.discard();
          return;
        }
        if (
          outcome.ok ||
          outcome.error === "program_cap_reached" ||
          outcome.error === "no_rebate_account"
        ) {
          applyOutcome(outcome);
          return;
        }
        showStepUpError();
      })
      .catch(() => {
        if (!controller.signal.aborted) showStepUpError();
      })
      .finally(() => {
        if (resumeAbort.current === controller) resumeAbort.current = null;
        inFlight.current = false;
      });
  }, [resume, applyOutcome, showStepUpError]);

  /**
   * Whether dismissing would lose work, which is what gates the discard
   * confirmation. On the gate that means typed or pinned input; past it, the
   * committed partners are themselves the unsaved work. Success is excluded
   * even though those partners are still held: the program exists by then, so
   * closing loses nothing and must not be questioned.
   */
  const isDirty =
    step === "lookup" ? gateDirty : step !== "success" && partners.length > 0;

  return {
    step,
    setStep,
    partners,
    gateResults,
    setGateDirty,
    isDirty,
    isTwoSub,
    relationshipIndex,
    currentRelationshipPartner: partners[relationshipIndex] ?? null,
    split,
    setSplit,
    split2,
    setSplit2,
    masterShare,
    submitting,
    submitError,
    submitErrorKind,
    submitErrorSubIbId,
    result,
    commitPartners,
    markLineupTouched,
    updateCurrentPartner,
    goToSplit,
    backFromRelationship,
    confirmSplit,
    backFromSplit,
    goToVerify,
    backFromVerify,
    cancelSubmit,
    doSubmit,
    stepUpControlled: Boolean(stepUp),
    stepUpPhase,
  };
}

export type UseCreateProgramWizardReturn = ReturnType<
  typeof useCreateProgramWizard
>;
